import React from 'react';
import { Banknote, CheckCheck, Download, Paperclip } from 'lucide-react';
import { formatCurrency } from '../../../utils/format';
import { formatDateForGroup } from '../../../utils/date';
import api from '../../../services/api';
import type { PurchaseOrder } from '../../../types/purchaseOrder';
import { useLanguage } from '../../../features/language/useLanguage';
import { Modal } from '../../../components/common/Modal';

interface PurchaseOrderMobileListProps {
  sortedDates: string[];
  groupedPOs: Record<string, PurchaseOrder[]>;
  currencySymbol: string;
  onEdit: (po: PurchaseOrder) => void;
  onSupplierClick: (supplierId: string) => void;
  onPay: (po: PurchaseOrder) => void;
  onReceive: (po: PurchaseOrder) => void;
  canWrite?: boolean;
}

export const PurchaseOrderMobileList: React.FC<PurchaseOrderMobileListProps> = ({
  sortedDates,
  groupedPOs,
  currencySymbol,
  onEdit,
  onSupplierClick,
  onPay,
  onReceive,
  canWrite = true,
}) => {
  const { t } = useLanguage();
  const [selectedProofPath, setSelectedProofPath] = React.useState<string | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = React.useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = React.useState<string | null>(null);

  const isImage = (path: string | null | undefined) => {
    if (!path) return false;
    const ext = path.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext || '');
  };

  const openPreview = async (path: string) => {
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    
    setSelectedProofPath(path);
    setIsPreviewModalOpen(true);
    
    if (isImage(path)) {
      try {
        const filename = path.split('/').pop() || path;
        const response = await api.get(`/uploads/documents/${filename}`, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([response.data]));
        setPreviewBlobUrl(url);
      } catch (err) {
        console.error("Failed to fetch preview", err);
      }
    }
  };

  React.useEffect(() => {
    return () => {
      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
    };
  }, [previewBlobUrl]);


  const handleDownload = async (path: string) => {
    try {
      const filename = path.split('/').pop() || path;
      const response = await api.get(`/uploads/documents/${filename}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert(t('purchaseOrder.paymentDetails.downloadFailed'));
    }
  };

  return (
    <div className="md:hidden space-y-6" data-test-id="po-mobile-list">
      {sortedDates.map(dateKey => (
        <div key={dateKey}>
          <h3 className="text-sm font-semibold text-gray-500 mb-3 ml-1">{formatDateForGroup(dateKey)}</h3>
          <div className="space-y-4">
            {groupedPOs[dateKey].map(po => (
              <div
                data-test-id={`po-mobile-card-${po.id}`}
                key={po.id}
                onClick={() => {
                  onEdit(po);
                }}
                className="bg-white p-4 rounded-lg shadow border border-gray-200 space-y-3 active:bg-gray-50 cursor-pointer"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-xs text-gray-500 block mb-1">#{po.orderNumber}</span>
                    <button
                      data-test-id={`po-mobile-supplier-button-${po.id}`}
                      onClick={event => {
                        event.stopPropagation();
                        onSupplierClick(po.supplier);
                      }}
                      className="font-medium text-primary-600 hover:text-primary-800"
                    >
                      {po.supplierName || t('settingsAdminMembers.unknownCompany')}
                    </button>
                  </div>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      po.status === 'draft'
                        ? 'bg-blue-100 text-blue-800'
                        : po.status === 'ordered'
                        ? 'bg-green-100 text-green-800'
                        : po.status === 'cancelled'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {t(`purchaseOrder.filters.options.${po.status}`, undefined, po.status)}
                  </span>
                </div>

                <div className="flex justify-between items-center text-sm pt-2 border-t border-gray-100">
                  <span className="text-gray-500">{t('salesOrder.list.totalAmount')}</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(po.totalAmount, currencySymbol)}</span>
                </div>

                {po.internalNotes && (
                  <p className="text-sm text-gray-500 italic bg-gray-50 p-2 rounded">{po.internalNotes}</p>
                )}

                {po.paymentProofUrl && (
                  <button
                    onClick={event => {
                      event.stopPropagation();
                      if (isImage(po.paymentProofUrl)) {
                        openPreview(po.paymentProofUrl!);
                      } else {
                        handleDownload(po.paymentProofUrl!);
                      }
                    }}
                    className="inline-flex items-center gap-2 text-sm text-primary-600 bg-primary-50 p-2 rounded w-full border border-primary-100"
                  >
                    <Paperclip className="h-4 w-4" />
                    <span className="truncate flex-1 text-left">{isImage(po.paymentProofUrl) ? t('purchaseOrder.actions.viewProof') : t('purchaseOrder.paymentDetails.proofOfPayment')}</span>
                  </button>
                )}



                <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                  {(po.paymentStatus ?? 'unpaid') === 'unpaid' && po.status !== 'cancelled' && canWrite && (
                    <button
                      data-test-id={`po-mobile-pay-button-${po.id}`}
                      onClick={event => {
                        event.stopPropagation();
                        onPay(po);
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors border border-emerald-200"
                    >
                      <Banknote className="h-3.5 w-3.5" />
                      {t('dashboard.outstandingPaymentsModal.markAsPaid')}
                    </button>
                  )}
                  {po.deliveryStatus !== 'received' && po.status !== 'cancelled' && canWrite && (
                    <button
                      data-test-id={`po-mobile-receive-button-${po.id}`}
                      onClick={event => {
                        event.stopPropagation();
                        onReceive(po);
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors border border-blue-200"
                    >
                      <CheckCheck className="h-3.5 w-3.5" />
                      {t('purchaseOrder.form.submit.receive')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Modal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        title={t('purchaseOrder.paymentDetails.proofOfPayment')}
        maxWidth="4xl"
      >
        <div className="flex flex-col items-center justify-center p-2 bg-gray-50 rounded-lg min-h-[40vh]">
          {isImage(selectedProofPath) ? (
            previewBlobUrl ? (
              <img 
                src={previewBlobUrl} 
                alt="Payment Proof Full" 
                className="max-w-full max-h-[60vh] object-contain rounded-md shadow-lg" 
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-gray-400">
                 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                 <span className="text-sm italic">{t('inventory.lotDetail.loadingShort')}</span>
              </div>
            )
          ) : (
            <div className="flex flex-col items-center gap-4 py-8">
              <Paperclip className="h-16 w-16 text-gray-300" />
              <p className="text-gray-500 font-medium truncate w-full text-center px-4">{selectedProofPath?.split('/').pop()}</p>
              <button
                onClick={() => handleDownload(selectedProofPath!)}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 shadow-sm"
              >
                <Download className="h-4 w-4" />
                {t('purchaseOrder.actions.downloadProof')}
              </button>
            </div>
          )}
        </div>
        
        {isImage(selectedProofPath) && (
          <div className="mt-4 flex flex-col gap-2 px-2">
            <span className="text-xs text-gray-500 font-mono truncate">{selectedProofPath?.split('/').pop()}</span>
            <button 
              onClick={() => handleDownload(selectedProofPath!)}
              className="w-full text-sm text-primary-600 font-medium hover:underline flex items-center justify-center gap-2 bg-white px-4 py-3 rounded-lg border border-gray-200 shadow-sm transition-colors active:bg-gray-50"
            >
              <Download className="h-4 w-4" /> {t('purchaseOrder.actions.downloadProof')}
            </button>
          </div>
        )}
      </Modal>
    </div>

  );
};
